import { captureException } from '@sentry/browser';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Checkbox as SemanticCheckbox } from 'semantic-ui-react';
import { BodyTextMedium } from '@modernhealth/vital-components';
import styled from '@emotion/styled';

import { TermsTypeKorea } from './ConsentTermsKorea';
import {
    ConsentTermsKoreaTestId,
    ConsentTextKoreaTestId,
    ConsentTestId,
    RegistrationFormTestId,
} from './testIds';
import { TermsTypeMain } from './ConsentTermsMain';
import { SingleSignOnFormTestId } from './SingleSignOnForm';
import { ExternalLink } from 'src/shared/components/ExternalLink';

type TermBoxType = TermsTypeMain | TermsTypeKorea;

interface TermBoxInfo {
    termsInfoStart?: string | null | React.ReactNode;
    termsInfoEnd?: string | null;
    firstAnchorText?: string | null;
    secondAnchorText?: string | null;
}
interface Props {
    type?: TermBoxType | never;
    checkboxTestId: RegistrationFormTestId | ConsentTermsKoreaTestId | SingleSignOnFormTestId;
    termsTestId?: ConsentTestId | ConsentTextKoreaTestId;
    onChange: () => void;
    isChecked: boolean;
    hideTosPrivacyUpdateMemberWeb?: boolean;
    shouldRender?: boolean;
    firstUrl?: string | null;
    secondUrl?: string | null;
}

export const ConsentTerms: React.FC<Props> = props => {
    const getTermBoxInfo = (
        termBoxType: TermBoxType,
        hideTosPrivacyUpdateMemberWeb: boolean,
    ): TermBoxInfo => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { t } = useTranslation();

        switch (termBoxType) {
            case TermsTypeMain.TOS: {
                const termsInfoStartTos = hideTosPrivacyUpdateMemberWeb
                    ? t('auth:registration_form.oldTermsInfo')
                    : t('auth:registration_form.termsInfo');
                const firstAnchorTextTos = hideTosPrivacyUpdateMemberWeb
                    ? t('auth:registration_form.oldTermsName')
                    : t('auth:registration_form.termsName');

                return { termsInfoStart: termsInfoStartTos, firstAnchorText: firstAnchorTextTos };
            }
            case TermsTypeMain.TOS_ENG: {
                if (hideTosPrivacyUpdateMemberWeb) {
                    return {
                        termsInfoStart: t('auth:registration_form.oldTermsInfo'),
                        firstAnchorText: t('auth:registration_form.oldTermsName'),
                    };
                } else {
                    return {
                        // this text is not translated on purpose - it's only for en-US users
                        termsInfoStart: (
                            <>
                                I agree to the{' '}
                                <ExternalLink
                                    href="https://www.modernhealth.com/terms-of-use"
                                    openInNewTab
                                >
                                    Terms of Use
                                </ExternalLink>
                                ,{' '}
                                <ExternalLink
                                    href="https://www.modernhealth.com/consent-to-telehealth-treatment"
                                    openInNewTab
                                >
                                    Consent to Telehealth Treatment
                                </ExternalLink>
                                , and{' '}
                                <ExternalLink
                                    href="https://www.modernhealth.com/e-sign-consent"
                                    openInNewTab
                                >
                                    E-Sign Consent
                                </ExternalLink>
                                .
                            </>
                        ),
                    };
                }
            }
            case TermsTypeMain.PRIVACY_POLICY:
                return {
                    termsInfoStart: t('auth:registration_form.privacyInfo'),
                    firstAnchorText: t('auth:registration_form.privacyName'),
                };
            case TermsTypeMain.HIPAA:
                return {
                    termsInfoStart: t('auth:registration_form.hipaaInfo'),
                    firstAnchorText: t('auth:registration_form.hipaaName'),
                };
            case TermsTypeMain.PRIVACY_POLICY_EURO:
                return {
                    termsInfoStart: t('auth:registration_form.privacyInfoEurope'),
                    termsInfoEnd: t('auth:registration_form.privacyInfoEndEurope'),
                    firstAnchorText: t('auth:registration_form.privacyNameEurope'),
                };
            case TermsTypeMain.PERSONAL_HEALTH_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.personalHealthInfo'),
                };
            case TermsTypeKorea.ACCEPT_ALL:
                return {
                    termsInfoStart: (
                        <Trans
                            ns="auth"
                            i18nKey="registration_form.consentToAll.Korea"
                            values={{ text: t('auth:registration_form.consentToAll.strong.Korea') }}
                            components={{ strong: <BodyTextMedium /> }}
                        />
                    ),
                };
            case TermsTypeKorea.TOS_KOREA:
                return {
                    termsInfoStart: t('auth:registration_form.privacyInfo.Korea'),
                    firstAnchorText: t('auth:registration_form.privacyName.Korea'),
                    secondAnchorText: t('auth:registration_form.privacyNameSecondary.Korea'),
                };
            case TermsTypeKorea.COLLECTION_AND_USE_OF_PERSONAL_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.collection.useOfPersonalInfo.Korea'),
                    firstAnchorText: t(
                        'auth:registration_form.collection.useOfPersonalInfoName.Korea',
                    ),
                };
            case TermsTypeKorea.COLLECTION_AND_USE_OF_SENSITIVE_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.collection.useOfSensitiveInfo.Korea'),
                    firstAnchorText: t(
                        'auth:registration_form.collection.useOfSensitiveInfoName.Korea',
                    ),
                };
            case TermsTypeKorea.PROVISION_OF_PERSONAL_INFO_TO_THIRD_PARTIES:
                return {
                    termsInfoStart: t('auth:registration_form.provisionPersonalInfo.Korea'),
                    firstAnchorText: t('auth:registration_form.provisionPersonalInfoName.Korea'),
                };
            case TermsTypeKorea.OVERSEAS_TRANSFER_OF_PERSONAL_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.overseasPersonalInfoXferInfo.Korea'),
                    firstAnchorText: t(
                        'auth:registration_form.overseasPersonalInfoXferInfoName.Korea',
                    ),
                };
            case TermsTypeKorea.OPTIONAL_COLLECTION_AND_USE_OF_PERSONAL_INFO_FOR_MARKETING:
                return {
                    termsInfoStart: t(
                        'auth:registration_form.collection.personalInfoForMarketingInfo.Korea',
                    ),
                    firstAnchorText: t(
                        'auth:registration_form.collection.personalInfoForMarketingInfoName.Korea',
                    ),
                };
            default:
                const exhaustiveCheck: never = termBoxType;
                captureException(new Error(`Undefined Consent Term type used: ${exhaustiveCheck}`));
                return {
                    termsInfoStart,
                    termsInfoEnd,
                    firstAnchorText,
                    secondAnchorText,
                };
        }
    };

    const {
        type,
        checkboxTestId,
        termsTestId,
        onChange,
        isChecked,
        shouldRender = true,
        firstUrl,
        secondUrl,
    } = props;

    if (!type || !shouldRender) {
        return null;
    }

    const { termsInfoStart, termsInfoEnd, firstAnchorText, secondAnchorText } = getTermBoxInfo(
        type,
        props.hideTosPrivacyUpdateMemberWeb ?? false,
    );

    return (
        <TermsBox>
            <Checkbox data-testid={checkboxTestId} onChange={onChange} checked={isChecked} />
            <TermsInfo data-testid={termsTestId}>
                {termsInfoStart}
                {firstUrl && (
                    <ExternalLink
                        data-testid={ConsentTermsKoreaTestId.FirstUrl}
                        onClick={() => window.open(firstUrl, '_blank')}
                    >
                        {firstAnchorText}
                    </ExternalLink>
                )}
                {secondUrl && (
                    <ExternalLink
                        data-testid={ConsentTermsKoreaTestId.SecondUrl}
                        onClick={() => window.open(secondUrl, '_blank')}
                    >
                        {secondAnchorText}
                    </ExternalLink>
                )}
                {termsInfoEnd}
            </TermsInfo>
        </TermsBox>
    );
};

const TermsInfo = styled('p')({
    marginLeft: 10,
});

const TermsBox = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 12,
    '& input': {
        flexShrink: 0,
    },
});

/**
 * @deprecated Use Vita's Checkbox instead.
 */
const Checkbox = styled(SemanticCheckbox)({
    width: 15,
    height: 15,
});
